export type Size = { width: number; height: number };
export type Rect = { x: number; y: number; width: number; height: number };
export type ViewState = { scale: number; cx: number; cy: number };

export const MIN_SCALE = 1;
export const MAX_SCALE = 8;
export const DEFAULT_VIEW: ViewState = { scale: 1, cx: 0.5, cy: 0.5 };

const HALF = 0.5;

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

export const isDefaultView = (view: ViewState): boolean => view.scale === MIN_SCALE;

export const fitRect = (viewport: Size, image: Size): Rect => {
  const scale = Math.min(viewport.width / image.width, viewport.height / image.height);
  const width = image.width * scale;
  const height = image.height * scale;
  return { x: (viewport.width - width) * HALF, y: (viewport.height - height) * HALF, width, height };
};

const clampOffset = (offset: number, size: number, viewportSize: number): number => size <= viewportSize
  ? (viewportSize - size) * HALF
  : clamp(offset, viewportSize - size, 0);

export const imageRect = (view: ViewState, fit: Rect, viewport: Size): Rect => {
  const width = fit.width * view.scale;
  const height = fit.height * view.scale;
  const x = clampOffset(viewport.width * HALF - view.cx * width, width, viewport.width);
  const y = clampOffset(viewport.height * HALF - view.cy * height, height, viewport.height);
  return { x, y, width, height };
};

export const normalizeView = (view: ViewState, fit: Rect, viewport: Size): ViewState => {
  const scale = clamp(view.scale, MIN_SCALE, MAX_SCALE);
  const rect = imageRect({ ...view, scale }, fit, viewport);
  return {
    scale,
    cx: (viewport.width * HALF - rect.x) / rect.width,
    cy: (viewport.height * HALF - rect.y) / rect.height,
  };
};

export type Point = { x: number; y: number };

export const zoomAt = (view: ViewState, fit: Rect, viewport: Size, factor: number, point: Point): ViewState => {
  const before = imageRect(view, fit, viewport);
  const scale = clamp(view.scale * factor, MIN_SCALE, MAX_SCALE);
  const anchorX = (point.x - before.x) / before.width;
  const anchorY = (point.y - before.y) / before.height;
  const width = fit.width * scale;
  const height = fit.height * scale;
  const x = point.x - anchorX * width;
  const y = point.y - anchorY * height;
  return normalizeView({
    scale,
    cx: (viewport.width * HALF - x) / width,
    cy: (viewport.height * HALF - y) / height,
  }, fit, viewport);
};

export const panBy = (view: ViewState, fit: Rect, viewport: Size, dx: number, dy: number): ViewState => {
  const rect = imageRect(view, fit, viewport);
  return normalizeView({
    scale: view.scale,
    cx: (viewport.width * HALF - (rect.x + dx)) / rect.width,
    cy: (viewport.height * HALF - (rect.y + dy)) / rect.height,
  }, fit, viewport);
};
